import { useTranslations } from "next-intl";
import { ListingItemAttributes } from "@/components/listing/listing-item-attributes";
import { Badge } from "@/components/ui/badge";
import type { DiscountType } from "@/features/cart/types/discount.type";
import { getLocalePrice } from "@/lib/utilities";
import { cn } from "@/lib/utils";
import { getDiscountDetails } from "../../helpers/get-discount-details";
import type { DietPricingComplex } from "../../types/diet-pricing-complex.type";
import { DiscountCodes } from "../discount-codes";

const ComplexPricing = ({
  priceComplex,
}: {
  priceComplex: DietPricingComplex;
}) => {
  const t = useTranslations();

  return (
    <div className="w-full md:mt-2">
      {priceComplex.discountCodes && (
        <div className="flex">
          <DiscountCodes
            className="mt-2 block md:hidden"
            codeCopy
            discountCodes={priceComplex.discountCodes}
          />
        </div>
      )}
      <div className="flex basis-full gap-2 lg:gap-0">
        <ListingItemAttributes className="block flex-1 items-end gap-2 whitespace-nowrap md:flex xl:block">
          <span className="flex items-center bg-brand-grey1 p-4 max-md:mt-4 max-md:flex-row-reverse max-md:justify-between max-md:rounded-lg md:flex-col md:items-start md:bg-transparent md:p-0">
            <span className="flex gap-1 md:-mt-1">
              <span className="font-normal text-base md:hidden">suma: </span>
              <span className="font-bold text-base">
                {getLocalePrice({
                  price: priceComplex.totalPrice ?? 0,
                })}
              </span>
              {priceComplex.discountCodes && (
                <DiscountCodes
                  className="ml-1 hidden md:block"
                  codeCopy
                  discountCodes={priceComplex.discountCodes}
                />
              )}
            </span>
            <span className="md:text-sm">
              {getLocalePrice({
                price: priceComplex.pricePerDay ?? 0,
              })}
              {` / ${t("diets.day")}`}
            </span>
          </span>
        </ListingItemAttributes>
      </div>
    </div>
  );
};

const SimplePricing = ({
  price,
  totalPrice,
  discountCodes,
  withDaysCount = false,
  hideOriginalPrice = false,
  showDiff = false,
  showTitle = true,
  title,
  numberOfDays,
}: {
  price?: number;
  discountCodes?: DiscountType[];
  totalPrice?: number;
  withDaysCount?: boolean;
  hideOriginalPrice?: boolean;
  showDiff?: boolean;
  showTitle?: boolean;
  title?: string;
  numberOfDays?: number;
}) => {
  const t = useTranslations();
  const { hasDiscount, priceAfterDiscount } = getDiscountDetails({
    price,
    discountCodes,
    numberOfDays,
    totalPrice,
  });

  // A discount code can still resolve to no actual reduction; only then is the
  // red price rendered below, so the regular one must stay in that case.
  const showsDiscountedPrice = Boolean(
    priceAfterDiscount && priceAfterDiscount !== price,
  );

  return (
    <div className="flex basis-1/2 gap-2 lg:gap-0">
      <div className="flex basis-1/2 gap-2 lg:gap-0">
        <ListingItemAttributes
          className="flex flex-1 flex-col whitespace-nowrap"
          classNameChildren="flex items-center gap-2"
          label={
            showTitle ? (title ? title : t("diets.price_from")) : undefined
          }
        >
          {/* With `hideOriginalPrice` a discounted price is shown on its own
              (red, below) instead of next to the struck-through regular one. */}
          {!(hideOriginalPrice && showsDiscountedPrice) && (
            <span
              className={cn("-mt-1 inline-block font-bold text-xl leading-6", {
                "mt-0 font-normal text-brand-grey5 text-md line-through":
                  hasDiscount,
              })}
            >
              {getLocalePrice({
                price: price ?? 0,
              })}
            </span>
          )}{" "}
          {!hasDiscount && !withDaysCount && `/ ${t("diets.day")}`}
          {priceAfterDiscount && priceAfterDiscount !== price && (
            <span className="flex items-center font-bold text-brand-red text-md">
              <span>
                {getLocalePrice({
                  price: priceAfterDiscount ?? 0,
                })}
              </span>
              {showDiff
                ? (() => {
                    const percent = 100 - (priceAfterDiscount * 100) / price!;
                    const rounded = Math.round(percent);
                    if (rounded <= 0) return null; // don't show 0% discounts
                    return (
                      <Badge
                        className={cn(
                          "ml-2 rounded-full border-0 bg-brand-red/10 px-3 py-1.5 text-brand-red md:px-2",
                        )}
                        variant="outline"
                      >
                        <p className="flex items-center gap-1 overflow-hidden text-ellipsis whitespace-nowrap font-normal text-xs leading-4 md:font-medium md:text-[12px]">
                          -{rounded}%
                        </p>
                      </Badge>
                    );
                  })()
                : !withDaysCount && (
                    <span className="font-normal"> / {t("diets.day")}</span>
                  )}
            </span>
          )}
        </ListingItemAttributes>
      </div>
    </div>
  );
};

const SummaryPricing = ({
  price,
  discountCodes,
  numberOfDays,
}: {
  price?: number;
  discountCodes?: DiscountType[];
  numberOfDays?: number;
}) => {
  const t = useTranslations();
  const { hasDiscount, priceAfterDiscount } = getDiscountDetails({
    price,
    discountCodes,
    numberOfDays,
  });

  return (
    <div className="flex basis-1/2 gap-2 lg:gap-0">
      <div className="flex basis-1/2 gap-2 lg:gap-0">
        <ListingItemAttributes
          className="block flex-1 items-end gap-2 whitespace-nowrap"
          label={t("common.summaryPrice")}
        >
          <span
            className={cn("-mt-1 inline-block font-bold text-xl leading-6", {
              "mt-0 font-normal text-md line-through": hasDiscount,
            })}
          >
            {getLocalePrice({
              price: price ?? 0,
            })}
          </span>{" "}
          {priceAfterDiscount && priceAfterDiscount !== price && (
            <span className={"font-bold text-brand-red text-md"}>
              <span>
                {getLocalePrice({
                  price: priceAfterDiscount ?? 0,
                })}
              </span>
            </span>
          )}
        </ListingItemAttributes>
      </div>
    </div>
  );
};

export const DietListItemPricing = ({
  price,
  totalPrice,
  numberOfDays,
  priceComplex,
  priceSummary,
  discountCodes,
  withDaysCount = false,
  hideOriginalPrice = false,
  showDiff = false,
  showTitle = true,
  title,
}: {
  price?: number;
  totalPrice?: number;
  numberOfDays?: number;
  priceSummary?: number;
  priceComplex?: DietPricingComplex;
  discountCodes?: DiscountType[];
  withDaysCount?: boolean;
  hideOriginalPrice?: boolean;
  showDiff?: boolean;
  showTitle?: boolean;
  title?: string;
}) => {
  if (priceComplex) {
    return <ComplexPricing priceComplex={priceComplex} />;
  }

  if (priceSummary) {
    return <SummaryPricing numberOfDays={numberOfDays} price={priceSummary} />;
  }

  if (withDaysCount) {
    return (
      <SimplePricing
        discountCodes={discountCodes}
        hideOriginalPrice={hideOriginalPrice}
        numberOfDays={numberOfDays}
        price={price}
        showTitle={showTitle}
        title={title}
        totalPrice={totalPrice}
        withDaysCount={withDaysCount}
      />
    );
  }

  return (
    <SimplePricing
      discountCodes={discountCodes}
      numberOfDays={numberOfDays}
      price={price}
      showDiff={showDiff}
      showTitle={showTitle}
      title={title}
      totalPrice={totalPrice}
    />
  );
};
